import { useTranslation } from 'react-i18next';

import Pagination from 'components/stateless/Pagination/Pagination';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import TableHeader from 'components/stateless/Table/TableHeader';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import TdLink from 'components/stateless/Table/TdLink';
import Tr from 'components/stateless/Table/Tr';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import { DEALER_AGREEMENT_STATUS } from 'enums';
import type { PageableType } from 'hooks/usePageable';
import type { DealerAgreementVOModel } from 'models/vo/DealerAgreementVO';

interface RelatedPartnerAgreementListProps {
  relatedAnchorAgreement: DealerAgreementVOModel[] | undefined;
  pageable: PageableType;
  paginate(pageNumber: number, sizePerPage: number): Promise<void>;
}

function RelatedPartnerAgreementList({ relatedAnchorAgreement, pageable, paginate }: RelatedPartnerAgreementListProps) {
  const { t } = useTranslation();

  const tableHeaders: HeaderType[] = [
    {
      headerText: t('text:Program_Type'),
      colWidths: 100,
    },
    {
      headerText: t('text:Partner_Master_Agreement_Number'),
      colWidths: 150,
    },
    {
      headerText: t('text:Currency'),
      colWidths: 100,
    },
    {
      headerText: t('text:Anchor_Name'),
      colWidths: 100,
    },
    {
      headerText: t('text:Responsible_Bank_Branch_Code'),
      colWidths: 120,
    },
    {
      headerText: t('text:Responsible_Bank_Branch_Name'),
      colWidths: 120,
    },
    {
      headerText: t('text:Effective_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Expired_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Agreement_Suspension'),
      colWidths: 100,
    },
    {
      headerText: '',
      colWidths: 50,
    },
  ];

  const getDealerAgreementStatusClassName = (dealerAgreementStatus: DEALER_AGREEMENT_STATUS) => {
    switch (dealerAgreementStatus) {
      case DEALER_AGREEMENT_STATUS.ACTIVATED:
        return 'text-bold-dark-green';
      case DEALER_AGREEMENT_STATUS.SUSPENDED:
        return 'text-bold-brick-red';
      default:
        return '';
    }
  };

  return (
    <div className="content-area">
      <SectionTitle title={t(`text:Uploaded_Dealer_Agreement`)} />
      <TableBorder>
        <TableHeader header={tableHeaders} />
        <TableBody numOfCol={tableHeaders.length}>
          {relatedAnchorAgreement?.map((item, i) => {
            return (
              <Tr key={i}>
                <Td data={t(`code:collateral-type.${item?.collateralType}`)} format="code" />
                <Td data={item.contractNo} />
                <Td data={item.currencyType} />
                <Td data={item.anchorFinancierClientName} />
                <Td data={item.branchCode} />
                <Td data={item.branchName} />
                <Td data={item.startDate} format="date" />
                <Td data={item.expiryDate} format="date" />
                <Td
                  data={t(`code:anchor-agreement-status.${item.dealerAgreementStatus}`)}
                  className={getDealerAgreementStatusClassName(item.dealerAgreementStatus)}
                  format="code"
                />
                <TdLink
                  path={ROUTES_FI.MANAGE_PARTNER.AGREEMENT_REGISTERED_DETAIL_BUILD_PATH(item.dealerAgreementId)}
                  state={{
                    dealerFinancierClientId: item.dealerFinancierClientId,
                    supportedCollateralType: item.collateralType,
                  }}
                />
              </Tr>
            );
          })}
        </TableBody>
      </TableBorder>
      <Pagination pageable={pageable} paginate={paginate} />
    </div>
  );
}

export default RelatedPartnerAgreementList;
